/**
 * 用同级 en 给 tokens（或 surface 列表）补 leading/trailing 标点。
 * 失败则 ok:false，调用方必须保持原 tokens（禁止猜插）。
 *
 * - surface 不变；标点只写在 leadingPunct / trailingPunct
 * - 游标贪心匹配；任一 surface 对不上 → 整题放弃
 * - 缩写/所有格内部的 . ' 属于 surface，不当句级标点
 */

/**
 * @typedef {{ surface: string, leadingPunct?: string, trailingPunct?: string, [k: string]: any }} EnrichedToken
 */

function charsEqualFold (a, b) {
  if (a === b) return true
  if (!a || !b) return false
  return a.toLowerCase() === b.toLowerCase()
}

function isSpace (c) {
  return !!c && /\s/.test(c)
}

function isWordStartChar (c) {
  return !!c && /[A-Za-z0-9]/.test(c)
}

function skipSpaces (text, i) {
  let p = i
  while (p < text.length && isSpace(text[p])) p++
  return p
}

/**
 * 在 text[start] 起匹配 surface（大小写不敏感；surface 空白 ↔ en 至少一处空白）
 * @returns {{ end: number } | null}
 */
export function matchSurfaceAt (text, start, surface) {
  const s = String(surface || '')
  if (!s) return null
  let i = start
  let j = 0

  while (j < s.length) {
    if (isSpace(s[j])) {
      while (j < s.length && isSpace(s[j])) j++
      if (i >= text.length || !isSpace(text[i])) return null
      while (i < text.length && isSpace(text[i])) i++
      continue
    }
    if (i >= text.length) return null
    if (!charsEqualFold(text[i], s[j])) return null
    i++
    j++
  }
  return { end: i }
}

/**
 * 对齐抽词：字母数字起始；允许内部 ' ’ -；点分缩写 U.S. / a.m. 保留点；句号不并进词
 * @param {string} text
 * @returns {string[]}
 */
export function extractAlignWords (text) {
  const s = String(text || '')
  if (!s) return []
  const out = []
  let i = 0
  while (i < s.length) {
    if (!isWordStartChar(s[i])) {
      i++
      continue
    }
    let j = i + 1
    while (
      j < s.length &&
      (/[A-Za-z0-9]/.test(s[j]) || s[j] === "'" || s[j] === '\u2019' || s[j] === '-')
    ) {
      j++
    }
    // 点分缩写：.[A-Za-z0-9]+ 重复，必要时收尾 .
    while (j < s.length && s[j] === '.' && j + 1 < s.length && /[A-Za-z]/.test(s[j + 1])) {
      j += 1
      while (j < s.length && /[A-Za-z0-9]/.test(s[j])) j++
    }
    if (j < s.length && s[j] === '.' && s.slice(i, j).includes('.')) {
      j += 1
    }
    out.push(s.slice(i, j))
    i = j
  }
  return out
}

function wordsCoverEn (en, surfaces) {
  const enToks = extractAlignWords(en)
  const surfaceToks = []
  for (let i = 0; i < surfaces.length; i++) {
    const parts = extractAlignWords(surfaces[i])
    for (let j = 0; j < parts.length; j++) surfaceToks.push(parts[j])
  }
  if (enToks.length !== surfaceToks.length) return false
  for (let i = 0; i < enToks.length; i++) {
    if (enToks[i].toLowerCase() !== surfaceToks[i].toLowerCase()) return false
  }
  return true
}

function collectLeadingPunct (text, start) {
  let i = skipSpaces(text, start)
  let punct = ''
  while (i < text.length && !isSpace(text[i]) && !isWordStartChar(text[i])) {
    punct += text[i]
    i++
  }
  return { punct, end: i }
}

function collectTrailingPunct (text, start) {
  let i = start
  let punct = ''
  while (i < text.length && !isSpace(text[i]) && !isWordStartChar(text[i])) {
    punct += text[i]
    i++
  }
  return { punct, end: i }
}

function stripPunctFields (t) {
  return {
    ...t,
    leadingPunct: '',
    trailingPunct: ''
  }
}

function normalizePassthrough (rawList) {
  const out = []
  for (let i = 0; i < rawList.length; i++) {
    const item = rawList[i]
    if (item && typeof item === 'object') {
      const surface = String(item.surface ?? '').trim()
      if (!surface) continue
      out.push({ ...item, surface, leadingPunct: '', trailingPunct: '' })
    } else {
      const surface = String(item ?? '').trim()
      if (!surface) continue
      out.push({ surface, leadingPunct: '', trailingPunct: '' })
    }
  }
  return out
}

/**
 * en 中不在已匹配 surface 区间内的非空白字符（句级标点）
 * @param {string} text
 * @param {{ start: number, end: number }[]} wordSpans
 */
function punctOutsideSpans (text, wordSpans) {
  const covered = new Array(text.length).fill(false)
  for (let s = 0; s < wordSpans.length; s++) {
    const { start, end } = wordSpans[s]
    for (let k = start; k < end && k < text.length; k++) covered[k] = true
  }
  let out = ''
  for (let i = 0; i < text.length; i++) {
    if (covered[i] || isSpace(text[i])) continue
    out += text[i]
  }
  return out
}

/**
 * @param {Array<{ surface?: string }|string>} tokensOrSurfaces
 * @param {string} en
 * @returns {{ ok: boolean, tokens: EnrichedToken[] }}
 */
export function enrichTokensPunctFromEn (tokensOrSurfaces, en) {
  const rawList = Array.isArray(tokensOrSurfaces) ? tokensOrSurfaces : []
  const text = String(en || '')
  if (!rawList.length || !text.trim()) {
    return { ok: false, tokens: normalizePassthrough(rawList) }
  }

  /** @type {EnrichedToken[]} */
  const base = []
  for (let i = 0; i < rawList.length; i++) {
    const item = rawList[i]
    if (item && typeof item === 'object') {
      const surface = String(item.surface ?? '').trim()
      if (!surface) continue
      base.push({
        ...item,
        surface,
        leadingPunct: '',
        trailingPunct: ''
      })
    } else {
      const surface = String(item ?? '').trim()
      if (!surface) continue
      base.push({ surface, leadingPunct: '', trailingPunct: '' })
    }
  }
  if (!base.length) return { ok: false, tokens: [] }

  const surfaces = base.map((t) => t.surface)
  if (!wordsCoverEn(text, surfaces)) {
    return { ok: false, tokens: base.map(stripPunctFields) }
  }

  let i = 0
  /** @type {{ start: number, end: number }[]} */
  const wordSpans = []

  for (let ti = 0; ti < base.length; ti++) {
    const lead = collectLeadingPunct(text, i)
    i = lead.end
    const matched = matchSurfaceAt(text, i, base[ti].surface)
    if (!matched) {
      return { ok: false, tokens: base.map(stripPunctFields) }
    }
    wordSpans.push({ start: i, end: matched.end })
    i = matched.end
    const trail = collectTrailingPunct(text, i)
    i = trail.end
    base[ti].leadingPunct = lead.punct
    base[ti].trailingPunct = trail.punct
  }

  i = skipSpaces(text, i)
  if (i < text.length) {
    const rest = text.slice(i)
    if (/[A-Za-z0-9]/.test(rest)) {
      return { ok: false, tokens: base.map(stripPunctFields) }
    }
    const extra = rest.replace(/\s+/g, '')
    if (extra) {
      base[base.length - 1].trailingPunct =
        String(base[base.length - 1].trailingPunct || '') + extra
    }
  }

  const expectedPunct = punctOutsideSpans(text, wordSpans)
  const gotPunct = base
    .map((t) => `${t.leadingPunct || ''}${t.trailingPunct || ''}`)
    .join('')
  if (expectedPunct !== gotPunct) {
    return { ok: false, tokens: base.map(stripPunctFields) }
  }

  return { ok: true, tokens: base }
}

/**
 * 将 enriched token 的旁路标点展开进 expectedWords 序列（供 QuestionInput punct 槽）
 * @param {EnrichedToken} token
 * @param {(surface: string, token: EnrichedToken) => string[]} expandSurface
 * @returns {string[]}
 */
export function expandEnrichedTokenToExpectedWords (token, expandSurface) {
  const out = []
  const lead = String(token?.leadingPunct || '')
  const trail = String(token?.trailingPunct || '')
  if (lead) out.push(lead)
  const parts = typeof expandSurface === 'function'
    ? expandSurface(String(token?.surface || ''), token)
    : [String(token?.surface || '').trim()].filter(Boolean)
  for (let i = 0; i < parts.length; i++) {
    if (parts[i]) out.push(parts[i])
  }
  if (trail) out.push(trail)
  return out
}
